import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import { EditIcon, DeleteIcon, AddIcon } from '../components/Icons';
import './AdminStoreCredits.css';

function AdminStoreCredits() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { success, error: showError } = useNotification();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [userIdFilter, setUserIdFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCredit, setEditingCredit] = useState(null);
  const [creditForm, setCreditForm] = useState({
    user_id: '',
    amount: '',
    source: 'manual',
    expires_at: '',
    notes: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-store-credits', page, limit, userIdFilter],
    queryFn: async () => {
      const limitParam = limit === 'all' ? '999999' : limit.toString();
      const params = new URLSearchParams({ page, limit: limitParam });
      if (userIdFilter) params.append('user_id', userIdFilter);
      const res = await apiClient.get(`/admin/store-credits?${params}`);
      return res.data;
    },
  });

  const createCreditMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/admin/store-credits', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-store-credits']);
      setShowCreateModal(false);
      setCreditForm({
        user_id: '',
        amount: '',
        source: 'manual',
        expires_at: '',
        notes: '',
      });
      success('Store credit sukurtas');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida kuriant store credit');
    },
  });

  const updateCreditMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      return apiClient.put(`/admin/store-credits/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-store-credits']);
      setEditingCredit(null);
      setCreditForm({
        user_id: '',
        amount: '',
        source: 'manual',
        expires_at: '',
        notes: '',
      });
      success('Store credit atnaujintas');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida atnaujinant store credit');
    },
  });

  const deleteCreditMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/admin/store-credits/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-store-credits']);
      success('Store credit pašalintas');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida šalinant store credit');
    },
  });

  const handleOpenCreateModal = () => {
    setEditingCredit(null);
    setCreditForm({
      user_id: '',
      amount: '',
      source: 'manual',
      expires_at: '',
      notes: '',
    });
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (credit) => {
    setEditingCredit(credit);
    setCreditForm({
      user_id: credit.user_id,
      amount: credit.amount,
      source: credit.source,
      expires_at: credit.expires_at ? credit.expires_at.split('T')[0] : '',
      notes: credit.notes || '',
    });
    setShowCreateModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!creditForm.user_id || !creditForm.amount) {
      notification.info('Užpildykite visus privalomus laukus');
      return;
    }

    if (editingCredit) {
      updateCreditMutation.mutate({
        id: editingCredit.id,
        data: {
          balance: creditForm.amount,
          expires_at: creditForm.expires_at || null,
          notes: creditForm.notes || null,
        },
      });
    } else {
      createCreditMutation.mutate({
        user_id: parseInt(creditForm.user_id),
        amount: parseFloat(creditForm.amount),
        source: creditForm.source,
        expires_at: creditForm.expires_at || null,
        notes: creditForm.notes || null,
      });
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Pašalinti store credit',
      message: 'Ar tikrai norite pašalinti šį store credit?',
      type: 'danger',
      confirmText: 'Pašalinti',
      onConfirm: () => {
        deleteCreditMutation.mutate(id);
        setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
      },
      onCancel: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('lt-LT');
  };

  const getSourceLabel = (source) => {
    const labels = {
      refund: 'Refund',
      compensation: 'Kompensacija',
      promotion: 'Promocija',
      manual: 'Rankinis',
    };
    return labels[source] || source;
  };

  if (isLoading) {
    return <div className="admin-store-credits-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-store-credits-page container">
      <div className="credits-header">
        <h1>Store Credits valdymas</h1>
        <button onClick={handleOpenCreateModal} className="btn btn-primary btn-sm">
          <AddIcon />
          Sukurti store credit
        </button>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Filtruoti pagal vartotojo ID:</label>
            <input
              type="text"
              value={userIdFilter}
              onChange={(e) => {
                setUserIdFilter(e.target.value);
                setPage(1);
              }}
              placeholder="Ieškoti pagal vartotojo ID..."
              className="input"
            />
          </div>
        </div>
      </div>

      {/* Store credits sąrašas */}
      <div className="credits-table-container">
        <table className="credits-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Vartotojas</th>
              <th>Suma</th>
              <th>Balansas</th>
              <th>Šaltinis</th>
              <th>Galioja iki</th>
              <th>Sukurta</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {data?.credits && data.credits.length > 0 ? (
              data.credits.map((credit) => (
                <tr key={credit.id}>
                  <td>{credit.id}</td>
                  <td>
                    {credit.user_email ? (
                      <div>
                        <div>{credit.user_email}</div>
                        {credit.first_name && credit.last_name && (
                          <div className="user-name">
                            {credit.first_name} {credit.last_name}
                          </div>
                        )}
                      </div>
                    ) : (
                      `ID: ${credit.user_id}`
                    )}
                  </td>
                  <td>{parseFloat(credit.amount).toFixed(2)} €</td>
                  <td>
                    <strong className={parseFloat(credit.balance) > 0 ? 'balance-positive' : 'balance-zero'}>
                      {parseFloat(credit.balance).toFixed(2)} €
                    </strong>
                  </td>
                  <td>{getSourceLabel(credit.source)}</td>
                  <td>
                    {credit.expires_at ? (
                      <span className={new Date(credit.expires_at) < new Date() ? 'expired' : ''}>
                        {formatDate(credit.expires_at)}
                      </span>
                    ) : (
                      'Nėra termino'
                    )}
                  </td>
                  <td>{formatDate(credit.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleOpenEditModal(credit)}
                        className="btn-icon btn-icon-secondary"
                        title="Redaguoti"
                      >
                        <EditIcon />
                      </button>
                      <button
                        onClick={() => handleDelete(credit.id)}
                        className="btn-icon btn-icon-danger"
                        title="Pašalinti"
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="empty-message">
                  Store credits nerasta
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginacija */}
      {data?.pagination && (
        <Pagination
          page={data.pagination.page}
          totalPages={data.pagination.pages}
          totalItems={data.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Sukūrimo/redagavimo modalas */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCredit ? 'Redaguoti store credit' : 'Sukurti store credit'}</h2>

            <form onSubmit={handleSubmit}>
              {!editingCredit && (
                <>
                  <div className="form-group">
                    <label>Vartotojo ID *</label>
                    <input
                      type="number"
                      value={creditForm.user_id}
                      onChange={(e) => setCreditForm({ ...creditForm, user_id: e.target.value })}
                      className="input"
                      required
                      placeholder="Įveskite vartotojo ID..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Suma (€) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={creditForm.amount}
                      onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                      className="input"
                      required
                      placeholder="0.00"
                    />
                  </div>

                  <div className="form-group">
                    <label>Šaltinis *</label>
                    <select
                      value={creditForm.source}
                      onChange={(e) => setCreditForm({ ...creditForm, source: e.target.value })}
                      className="input"
                      required
                    >
                      <option value="manual">Rankinis</option>
                      <option value="refund">Refund</option>
                      <option value="compensation">Kompensacija</option>
                      <option value="promotion">Promocija</option>
                    </select>
                  </div>
                </>
              )}

              {editingCredit && (
                <>
                  <div className="form-group">
                    <label>Balansas (€) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={creditForm.amount}
                      onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                      className="input"
                      required
                      placeholder="0.00"
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Galioja iki</label>
                <input
                  type="date"
                  value={creditForm.expires_at}
                  onChange={(e) => setCreditForm({ ...creditForm, expires_at: e.target.value })}
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>Pastabos</label>
                <textarea
                  value={creditForm.notes}
                  onChange={(e) => setCreditForm({ ...creditForm, notes: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Papildomos pastabos..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingCredit(null);
                    setCreditForm({
                      user_id: '',
                      amount: '',
                      source: 'manual',
                      expires_at: '',
                      notes: '',
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Atšaukti
                </button>
                <button
                  type="submit"
                  disabled={createCreditMutation.isLoading || updateCreditMutation.isLoading}
                  className="btn btn-primary"
                >
                  {createCreditMutation.isLoading || updateCreditMutation.isLoading
                    ? 'Išsaugoma...'
                    : editingCredit
                    ? 'Atnaujinti'
                    : 'Sukurti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
      />
    </div>
  );
}

export default AdminStoreCredits;
